import React, { useState } from "react";
import { Button, Input, Form, Modal, Typography, Flex, Select } from "antd";
import { PlusOutlined, MinusCircleOutlined, EditOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Error, Success } from "../../../commons/Messages/Messages";
import { FIELD_TYPES, fieldType } from "./ticketFields";

import { useTranslation } from "react-i18next";
const { Text } = Typography;

const FIELD_TYPE_LABELS = { String: "Text", Number: "Number", Date: "Date" };

// Creates a ticket group on the current board, or edits `group` (name and custom columns).
const AddTicket = ({ updateParent, group }) => {
  const { t } = useTranslation("dashboard");
  const { id } = useParams();
  const isEdit = Boolean(group);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const showModal = () => {
    form.setFieldsValue(
      isEdit
        ? {
            title: group.title,
            Coloumn: (group.fields || []).map(f => ({ name: f.name, type: fieldType(f) }))
          }
        : { title: undefined, Coloumn: [] }
    );
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onFinish = async values => {
    try {
      setLoading(true);
      const body = { title: values.title, fields: values.Coloumn || [] };
      const headers = { Authorization: "Bearer " + localStorage.getItem("token") };
      const res = isEdit
        ? await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/ticketGroups/update/${group._id}`,
            body,
            { headers }
          )
        : await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/ticketGroups/add`,
            { ...body, boardID: id },
            { headers }
          );
      Success(t(res.data.successMessage));
      updateParent?.();
      handleCancel();
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isEdit ? (
        <Button type="text" icon={<EditOutlined />} onClick={showModal}>
          {t("Edit Group")}
        </Button>
      ) : (
        <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
          {t("New Group")}
        </Button>
      )}

      <Modal
        title={
          <div className="tkt-modal-title">
            <span>{isEdit ? t("Edit Group") : t("Add Group")}</span>
            <span className="tkt-modal-subtitle">{t("Define the group schema.")}</span>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        confirmLoading={loading}
        okText={isEdit ? t("Save") : t("Create")}
        cancelText={t("Cancel")}
        width={480}
        centered
        forceRender
        rootClassName="tkt-modal"
      >
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="title"
            label={<Text strong>{t("Group Name")}</Text>}
            rules={[{ required: true, whitespace: true, message: t("Required") }]}
          >
            <Input size="large" placeholder={t("E.g. Bug Fixes")} />
          </Form.Item>

          <div className="tkt-modal-section">
            <Text strong className="tkt-modal-section__title">
              {t("Custom Columns")}
            </Text>
            <Form.List name="Coloumn">
              {(fields, { add, remove }) => (
                <Flex vertical gap="small">
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="tkt-column-row">
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        rules={[
                          { required: true, whitespace: true, message: t("Required") },
                          ({ getFieldValue }) => ({
                            validator: (_, value) => {
                              const names = (getFieldValue("Coloumn") || []).map(c =>
                                c?.name?.trim().toLowerCase()
                              );
                              const trimmed = value?.trim().toLowerCase();
                              if (trimmed && (trimmed.includes(".") || trimmed.startsWith("$"))) {
                                return Promise.reject(
                                  new window.Error(
                                    t('Column names cannot contain "." or start with "$"')
                                  )
                                );
                              }
                              return trimmed && names.filter(n => n === trimmed).length > 1
                                ? Promise.reject(new window.Error(t("Column names must be unique")))
                                : Promise.resolve();
                            }
                          })
                        ]}
                        className="tkt-column-row__name"
                      >
                        <Input placeholder={t("Column Name")} aria-label={t("Column Name")} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "type"]}
                        initialValue="String"
                        rules={[{ required: true, message: t("Required") }]}
                        className="tkt-column-row__type"
                      >
                        <Select
                          aria-label={t("Type")}
                          options={FIELD_TYPES.map(type => ({
                            value: type,
                            label: t(FIELD_TYPE_LABELS[type])
                          }))}
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        aria-label={t("Remove")}
                        onClick={() => remove(name)}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ type: "String" })}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t("Add Field")}
                  </Button>
                </Flex>
              )}
            </Form.List>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default AddTicket;
